/**
 * Patient medical file — patient uploads + orders with examination results
 */
import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import moment from 'moment';
import {
  useDeletePatientMedicalAttachmentMutation,
  useGetPatientMedicalFileQuery,
  useUploadPatientMedicalAttachmentMutation,
} from '../../store/api/orderResultsApi';
import OrderResultsPanel from '../orders/OrderResultsPanel';
import { StatusChip } from '../common';
import type { OrderStatus } from '../../types/order';

interface PatientMedicalFileSectionProps {
  patientId: number;
  canManage?: boolean;
}

const PatientMedicalFileSection = ({
  patientId,
  canManage = false,
}: PatientMedicalFileSectionProps) => {
  const { data, isLoading, refetch } = useGetPatientMedicalFileQuery({
    patientId,
    page_size: 50,
  });
  const [uploadAttachment, { isLoading: isUploading }] =
    useUploadPatientMedicalAttachmentMutation();
  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeletePatientMedicalAttachmentMutation();

  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [attachmentName, setAttachmentName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const orders = data?.results || [];
  const patientAttachments = data?.patient_attachments || [];

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    const name = attachmentName.trim() || file.name;
    const formData = new FormData();
    formData.append('name', name);
    formData.append('file', file);

    try {
      await uploadAttachment({ patientId, formData }).unwrap();
      toast.success('Attachment uploaded');
      setAttachmentName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetch();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'Failed to upload attachment');
    }
  };

  const handleDelete = async (attachmentId: number) => {
    try {
      await deleteAttachment({ patientId, attachmentId }).unwrap();
      toast.success('Attachment deleted');
      refetch();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'Failed to delete attachment');
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Medical File
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Patient-uploaded documents and orders with examination results.
        </Typography>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Patient attachments
        </Typography>

        {canManage && (
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              label="Attachment name"
              value={attachmentName}
              onChange={(e) => setAttachmentName(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button
              variant="outlined"
              startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => handleUpload(e.target.files?.[0] || null)}
            />
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : patientAttachments.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            No patient-uploaded attachments yet.
          </Typography>
        ) : (
          <Box sx={{ mb: 3 }}>
            {patientAttachments.map((file) => (
              <Box
                key={file.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box>
                  <Typography variant="body2">
                    <Link href={file.file_url || file.file} target="_blank" rel="noopener">
                      {file.name}
                    </Link>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {moment(file.created_at).format('MMM D, YYYY HH:mm')}
                  </Typography>
                </Box>
                {canManage && (
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isDeleting}
                    onClick={() => handleDelete(file.id)}
                    aria-label="Delete attachment"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Orders & examination results
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary">No orders with examination results yet.</Typography>
        ) : (
          orders.map((order) => {
            const isExpanded = expandedOrderId === order.id;

            return (
              <Box
                key={order.id}
                sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                >
                  <Box>
                    <Typography fontWeight={600}>{order.order_number}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[order.service_name, order.subservice_name].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {moment(order.scheduled_date).format('MMM D, YYYY')}
                      {order.provider_name ? ` · ${order.provider_name}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" label="Has result" color="success" />
                    <StatusChip status={order.status as OrderStatus} />
                    <Button
                      size="small"
                      endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {isExpanded ? 'Hide' : 'Result'}
                    </Button>
                  </Box>
                </Box>

                <Collapse in={isExpanded}>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {order.result?.attachments?.length ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Uploaded files
                        </Typography>
                        {order.result.attachments.map((file) => (
                          <Typography key={file.id} variant="body2">
                            <Link href={file.file_url || file.file} target="_blank" rel="noopener">
                              {file.name}
                            </Link>
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                    <OrderResultsPanel orderId={order.id} compact />
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default PatientMedicalFileSection;
